import React, { useState, useEffect, useMemo } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AddchartIcon from "@mui/icons-material/Addchart";
import TrafficIcon from "@mui/icons-material/Traffic";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MainHeader from "./MainHeader";
import "./MainMenu.scss";
import logo from "../assests/Aayslogo.png";
import { Link, useNavigate } from "react-router-dom";
import HomeFilledIcon from "@mui/icons-material/HomeFilled";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import Tooltip from "@mui/material/Tooltip";
import { Network, LineChart, Boxes } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const drawerWidth = 240;
 
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});
 
const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});
 
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": {
          ...openedMixin(theme),
          background: "linear-gradient(180deg, #e9e9f5 0%, #fff 100%)",
        },
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": {
          ...closedMixin(theme),
          background: "linear-gradient(180deg, #e9e9f5 0%, #fff 100%)",
        },
      }),
}));
 
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isPadding",
})(({ theme, open, isPadding }) => ({
  flexGrow: 1,
  padding: isPadding ? theme.spacing(3) : 0,
  height: "calc(100vh - 14px)",
  overflow: "hidden",
  overflowY: "auto",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

// Icon mapping
const iconMap = {
  HomeIcon: <HomeFilledIcon sx={{ fontSize: 24 }} />,
  DashboardIcon: <DashboardIcon sx={{ fontSize: 24 }} />,
  DomainIcon: <Network size={24} />,
  StorageIcon: <Boxes size={24} />,
  TrafficIcon: <TrafficIcon sx={{ fontSize: 24 }} />,
  LeaderboardIcon: <LineChart size={24} />,
  PendingActionsIcon: <PendingActionsIcon sx={{ fontSize: 24 }} />,
  FeatureIcon: <AddchartIcon sx={{ fontSize: 24 }} />,
  accessManagement: <ManageAccountsIcon sx={{ fontSize: 24 }} />,
};

// Menu item styles
const getMenuItemStyles = (isActive, open) => ({
  listItemButton: {
    minHeight: 48,
    justifyContent: open ? "initial" : "center",
    color: "#bb1ccc",
    background: isActive ? "linear-gradient(90deg, #e6e6fd 0%, #d4d4d6 100%)" : "transparent",
    borderLeft: isActive ? "4px solid #bb1ccc" : "4px solid transparent",
    mb: 0.5,
    ml: 1,
    mr: 1,
    "&:hover": {
      background: "linear-gradient(90deg, #e6e6fd 0%, #d4d4d6 100%)",
      borderLeft: "4px solid #cdcdf2",
    },
  },
  listItemIcon: {
    minWidth: 0,
    mr: open ? 1.5 : "auto",
    justifyContent: "center",
    color: isActive ? "#bb1ccc" : "black",
  },
  listItemText: {
    opacity: open ? 1 : 0,
    color: isActive ? "#bb1ccc" : "black",
  },
});

// All roles constant
const ALL_ROLES = ["Admin", "Product Owner", "Data Steward", "Data Engineer", "Vendor", "Domain Owner"];

export default function MainMenu({ children, text1, text2, padding }) {
  const { authUser } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [role, setRole] = useState("Product Owner");

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const currentPath = window.location.pathname;

  useEffect(() => {
    if (authUser?.role) {
      setRole(authUser.role);
    }
  }, [authUser]);

  // Main menu items (above divider)
  const mainMenuItems = [
    { name: "Home", path: "/", icons: "HomeIcon", roles: ALL_ROLES },
    { name: "Overview", path: "/Dashboard", icons: "DashboardIcon", roles: ALL_ROLES },
    { name: "Domain Management", path: "/dataDomain", icons: "DomainIcon", roles: ALL_ROLES },
    { name: "Data Discovery", path: "/data-contract", icons: "StorageIcon", roles: ALL_ROLES },
    { name: "Data Traffic Lights", path: "/data-traffic-lights", icons: "TrafficIcon", roles: ALL_ROLES },
    { name: "Data Monitoring", path: "/data-monitoring", icons: "LeaderboardIcon", roles: ALL_ROLES },
    { name: "Actions", path: "/action", icons: "PendingActionsIcon", roles: ALL_ROLES },
  ];

  // Bottom menu items (below divider)
  const bottomMenuItems = [
    { name: "Leave feedback", path: "/feature-requests", icons: "FeatureIcon", roles: ALL_ROLES },
    { name: "Access Management", path: "/accessManagement", icons: "accessManagement", roles: ["Admin"] },
  ];

  const mainMenuList = useMemo(() => {
    return mainMenuItems.filter((item) => item.roles.includes(role));
  }, [role]);

  const bottomMenuList = useMemo(() => {
    return bottomMenuItems.filter((item) => item.roles.includes(role));
  }, [role]);

  // Render menu item
  const renderMenuItem = ({ name, icons, path }) => {
    const isActive = currentPath === path;
    const styles = getMenuItemStyles(isActive, open);

    if (path === "/feature-requests") {
      return (
        <ListItem disablePadding sx={{ display: "block" }} key={name}>
          <Tooltip title={!open ? name : ""} placement="right" arrow>
            <ListItemButton onClick={() => navigate(path)} sx={styles.listItemButton}>
              <ListItemIcon sx={styles.listItemIcon}>{iconMap[icons]}</ListItemIcon>
              <ListItemText primary={name} sx={styles.listItemText} />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <Link style={{ textDecoration: "none" }} to={path} key={name}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <Tooltip title={!open ? name : ""} placement="right" arrow>
            <ListItemButton sx={styles.listItemButton}>
              <ListItemIcon sx={styles.listItemIcon}>{iconMap[icons]}</ListItemIcon>
              <ListItemText primary={name} sx={styles.listItemText} />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </Link>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <MainHeader open={open} handleDrawerOpen={handleDrawerOpen} text={text1} text2={text2} />

      <Drawer variant="permanent" open={open}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <DrawerHeader
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "space-between" : "center",
              px: open ? 2 : 0,
            }}
          >
            <img src={logo} alt="logo" style={{ height: 30 }} />

            {open && (
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === "ltr" ? (
                  <ChevronLeftIcon sx={{ color: "#bb1ccc" }} />
                ) : (
                  <ChevronRightIcon sx={{ color: "#bb1ccc" }} />
                )}
              </IconButton>
            )}
          </DrawerHeader>

          <Divider />

          <List>{mainMenuList.map(renderMenuItem)}</List>

          {/* Spacer to push bottom menu to bottom */}
          <Box sx={{ flexGrow: 1 }} />

          {bottomMenuList.length > 0 && (
            <Divider sx={{ mx: 1, borderColor: "rgba(0, 0, 160, 0.2)" }} />
          )}

          <List sx={{ pb: 2 }}>{bottomMenuList.map(renderMenuItem)}</List>
        </Box>
      </Drawer>

      <Main sx={{ backgroundColor: "#f9fafb" }} open={open} isPadding={padding}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}
 
 